import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import throttle from 'lodash.throttle';
import moment from 'moment';
import cn from 'classnames';

import { IS_LIST_ID } from 'common/regex';
import { constructMSAImageUrl } from 'helpers';
import { isDesktop, offset } from 'helpers/ClientUtils';
import { onEvent } from 'helpers/EventHelpers';
import { toUSD } from 'helpers/NumberFormats';
import { Loader } from 'components/Loader';
import CollectionQuickview from 'components/account/CollectionQuickview';
import { clearHeartList, fetchHeartList, fetchListInfo, getHeartCounts, sharingList } from 'actions/hearts';
import modalCSS from 'styles/components/common/modal.scss';
import css from 'styles/containers/account/collection.scss';

const PRODUCT_IMAGE_SIZE = 700;
const LAZYLOAD_HEARTS_OFFSET = 500;
const msaImageDimensions = { width: PRODUCT_IMAGE_SIZE };

export class Collection extends Component {
  static contextTypes = {
    testId: PropTypes.func,
    marketplace: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.loadMore = createRef();
    this.onScrollPage = throttle(this.onScrollPage.bind(this), 100);
  }

  state = {
    isShareModalOpen: false,
    shareToken: null,
    isShareLinkLoading: false,
    currentItem: null,
    isSharedCollection: false
  }

  componentDidMount() {
    const { fetchListInfo, fetchHeartList, params: { id } } = this.props;

    const isSharedCollection = !IS_LIST_ID.test(id);

    this.setState({
      isSharedCollection,
      listParams: isSharedCollection ? { shareToken: id } : { listId: id }
    }, () => {
      const { listParams } = this.state;

      fetchHeartList(listParams).then(this.onGetHeartCounts);
      fetchListInfo(listParams);
    });

    this.setupScrollHandler();
  }

  componentWillUnmount() {
    const { clearHeartList } = this.props;
    clearHeartList();
  }

  onScrollPage = () => {
    const { scrollY, pageYOffset, innerHeight } = window;
    const loadMore = this.loadMore.current;

    if (!loadMore) {
      return this.scrollHandler.unbind();
    }

    const { fetchHeartList, hearts: { nextPageToken } } = this.props;
    const { listParams } = this.state;

    const offsetScroll = (scrollY || pageYOffset) + innerHeight + LAZYLOAD_HEARTS_OFFSET;
    if (offsetScroll > offset(loadMore).top) {
      // unbind scroll while we fetch more hearts
      this.scrollHandler.unbind();
      fetchHeartList({ ...listParams, nextPageToken, concat: true }).then(() => {
        this.onGetHeartCounts();

        // rebind scroll handler after more hearts have been received
        if (loadMore) {
          this.setupScrollHandler();
        }
      });
    }
  }

  onGetHeartCounts = () => {
    const { getHeartCounts, hearts: { hearts } } = this.props;

    if (!hearts) {
      return null;
    }

    getHeartCounts(hearts);
  }

  onShare = ({ currentTarget }) => {
    const { sharingList } = this.props;
    const { dataset: { listId } } = currentTarget;
    this.setState({ isShareLinkLoading: true });
    sharingList(listId).then(({ shareToken }) => {
      this.setState({ isShareModalOpen: true, isShareLinkLoading: false, shareToken });
    });
  }

  onCloseShareModal = () => {
    this.setState({ isShareModalOpen: false });
  }

  onCloseProductModal = () => {
    this.setState({ currentItem: null });
  }

  onCopyShareLink = e => {
    e.preventDefault();
    const { currentTarget: { shareLink } } = e;
    shareLink.select();
    shareLink.focus();
    document.execCommand('copy');
  }

  onProductClick = item => {
    this.setState({ currentItem: item });
  }

  setupScrollHandler = () => {
    this.scrollHandler = onEvent(window, 'scroll', this.onScrollPage, null, this);
  }

  makeImage = item => {
    const { imageId, productName } = item;
    const imagePath = constructMSAImageUrl(imageId, msaImageDimensions);

    return <button type="button" onClick={this.onProductClick.bind(this, item)}><img src={imagePath} alt={productName} /></button>;
  }

  makePrice = ({ price, listPrice }) => {
    const isOnSale = listPrice > price;

    if (isOnSale) {
      return (
        <>
          <span className={css.salePrice}>
            {toUSD(price)}
          </span>
          <span className={css.listPrice}>
            msrp: {toUSD(listPrice)}
          </span>
        </>
      );
    }

    return (
      <span className={css.price}>
        {toUSD(listPrice)}
      </span>
    );
  }

  reorderHearts = () => {
    const { hearts: { hearts } } = this.props;

    // re-order logic ~stolen~ borrowed from https://github.com/jessekorzan/masonry-css-js
    const cols = 4;
    const reOrdered = [];
    let col = 0;
    while (col < cols) {
      for (let i = 0; i < hearts.length; i += cols) {
        const _val = hearts[i + col];
        if (_val !== undefined) {
          reOrdered.push(_val);
        }
      }
      col++;
    }

    return reOrdered;
  }

  render() {
    const {
      heartsList,
      hearts: { list = {}, hearts: reduxHearts, nextPageToken }
    } = this.props;

    const { marketplace } = this.context;

    if (!reduxHearts) {
      return <Loader />;
    }

    const { name, createTime, listId } = list;

    const { isShareModalOpen, isShareLinkLoading, shareToken, isSharedCollection, currentItem } = this.state;

    // only reorder if its a desktop view
    const hearts = isDesktop(marketplace) ? this.reorderHearts() : reduxHearts;

    return (
      <div className={css.wrapper}>
        { currentItem && <CollectionQuickview onClose={this.onCloseProductModal} item={currentItem} /> }

        { !isSharedCollection &&
          <Modal
            className={cn(modalCSS.modalContent, css.modal)}
            overlayClassName={modalCSS.modalOverlay}
            isOpen={isShareModalOpen}>

            <div className={cn(modalCSS.header, css.header)}>
              <h2>Share</h2>
              <button
                type="button"
                className={modalCSS.close}
                onClick={this.onCloseShareModal}
                aria-label="Close"/>
            </div>

            <form method="post" className={css.fields} onSubmit={this.onCopyShareLink}>
              <input
                name="shareLink"
                type="text"
                aria-label="Share Link"
                defaultValue={shareToken ? `${window?.origin}/account/favorites/${shareToken}` : 'Getting link...'}
                readOnly />
              <button type="submit">Copy</button>
            </form>
          </Modal>
        }

        <div className={css.container}>
          <div className={css.header}>
            <div>
              { name && <h1>{name}</h1> }
              { createTime && <p className={css.date}>{moment(createTime).format('MMMM D, YYYY')}</p> }
            </div>

            { !isSharedCollection &&
              <button
                type="button"
                className={css.share}
                data-list-id={listId}
                aria-expanded={isShareModalOpen}
                onClick={this.onShare}>{ isShareLinkLoading ? 'Getting Link...' : 'Share' }</button>
            }
          </div>
          <div className={css.cards}>
            { hearts.map(heart => {
              const { styleId } = heart;

              return (
                <div key={styleId} className={css.card}>
                  <div>{ this.makeImage(heart) }</div>
                  <div className={css.info}>
                    <span className={css.heart}>{ heartsList[styleId] }</span>
                    <p>{ this.makePrice(heart) }</p>
                  </div>
                </div>
              );
            })}

            { !!nextPageToken &&
            <button
              type="button"
              aria-hidden="true"
              className={css.loadMore}
              ref={this.loadMore}>
              More
            </button>
            }
          </div>
        </div>
      </div>
    );
  }
}

export const mapStateToProps = state => ({
  hearts: state.hearts,
  heartsList: state.products.heartsList
});

export default connect(mapStateToProps, {
  fetchHeartList,
  clearHeartList,
  fetchListInfo,
  sharingList,
  getHeartCounts
})(Collection);
